/**
 * Mistakes become exercises.
 *
 * Five forms, because one mechanic gets boring faster than the mistakes run
 * out. Always tapping the right spelling out of two buttons is answered after
 * the third time without reading:
 *
 *   1. FIND — the sentence is there, one word in it is wrong. Elmar taps it.
 *      This is the exercise closest to writing: first to notice THAT
 *      something is not right.
 *   2. UPPER OR LOWER CASE — the sentence is there, tapping toggles a word.
 *      Needs no invented alternatives; the sentence itself is the exercise.
 *   3. SPELLING — one word in two or three versions, one of them is right.
 *   4. IS THAT RIGHT? — one sentence, two buttons. Half of them are correct,
 *      so that leaving a right sentence alone is practised too.
 *   5. COMPLETE — the word opened at the place of the mistake: the stem
 *      stands there, the ending is chosen. "Strah…" against "Stra…" is the
 *      rule and nothing else.
 *   6. STRATEGY — the word, written correctly, and the question which
 *      strategy finds its spelling: Schwingen, Verlängern, Ableiten,
 *      Zerlegen, Merken. School asks for these by name, and his class test
 *      showed he did not know them.
 *   7. NOT A WORD — three spellings, two of them real words with different
 *      meanings (reisen, reißen), one of them nothing. Which one does not
 *      exist? The inverse of SPELLING, straight from the class test, and
 *      harder: it takes the rule AND the vocabulary.
 *
 * Which form a mistake gets depends on its kind, on whether a sentence is
 * known for it, and on the counter — see `kindFor`. Without `context` the
 * forms that need a sentence drop out.
 */

import type { Mistake, Word, MistakeKind, MoreExercise } from '../types.js'
import { choices, opened, endings, nonWord, shuffle, type Source } from './variants.js'

/**
 * Which generation of the exercise prompt counts as current.
 *
 * Must match SENTENCE_GENERATION in functions/api/exercises.js. A sentence
 * from an older generation counts as missing, and the app fetches a new one —
 * otherwise a sentence written once would stay for ever, including the ones
 * from a prompt that had since been put right.
 */
export const SENTENCE_GENERATION = 5

/** Is the sentence from the current generation of the prompt? */
const isCurrent = (gen?: number): boolean => (gen ?? 0) >= SENTENCE_GENERATION

/**
 * A mistake, wherever it comes from.
 *
 * Photographed pages deliver `Mistake`, typed texts `Word` — both describe
 * the same thing: a misspelled word, its correct form, and at best the
 * sentence that goes with it. The exercises work with this one form, so they
 * do not have to be built twice.
 */
export interface Issue {
  id: string
  wrong: string
  right: string
  kind: MistakeKind | string
  /**
   * The EXERCISE SENTENCE — written by the model, otherwise free of mistakes.
   *
   * Not Elmar's own sentence. That one contains further mistakes and is often
   * only an excerpt without a beginning; with upper and lower case the
   * question is then not even answerable, because you cannot see whether a
   * word stands at the start of the sentence. And what a child reads while
   * practising sinks in.
   *
   * Empty means: there is no sentence exercise for this mistake yet.
   */
  sentence: string
  /**
   * The word as it stands IN THE EXERCISE SENTENCE — and its correct form.
   *
   * Not necessarily `wrong`/`right`: if his own word fits into no natural
   * sentence, the model picks another one of the same kind of mistake. What
   * is practised is the rule, not the vocabulary item.
   */
  sentenceWrong: string
  sentenceRight: string
  /**
   * The sentence from HIS own text, in which the mistake stood.
   *
   * Not for showing — it holds further mistakes and is often only an excerpt.
   * It goes to the model, which reads it, finds the mistakes itself and
   * builds the exercises from them.
   */
  context: string
  /**
   * The spelling strategy the exercise sentence practises, and the tip that
   * applies it to the word. Both from the model, both may be empty for a
   * sentence from before generation 5.
   */
  strategy: string
  tip: string
  /**
   * The form this issue takes in the round, when the round has decided it —
   * see `askEveryStrategy`. Unset means: the counter decides, as usual.
   */
  form?: ExerciseKind
  at: number
  source: Source
}

/** A finding from a photographed page. */
export const fromPhoto = (m: Mistake): Issue => ({
  id: m.id,
  wrong: m.wrong,
  right: m.right,
  // The kind that the exercise sentence really practises — it can differ from
  // the reported one if the model changed the form of the word.
  kind: m.sentenceKind || m.kind,
  sentence: isCurrent(m.sentenceGen) ? (m.sentence ?? '') : '',
  sentenceWrong: m.sentenceWrong || m.wrong,
  sentenceRight: m.sentenceRight || m.right,
  context: m.context ?? '',
  strategy: strategyFor(m.sentenceKind || m.kind, m.sentenceRight || m.right, m.sentenceStrategy),
  tip: m.sentenceTip ?? '',
  at: m.at,
  source: 'photo',
})

/**
 * A practised word from a typed text.
 *
 * Here the kind of mistake does not come from the model but from the
 * LanguageTool rule — see `kindFromRule`. As the point in time `first`
 * counts: when he made the mistake for the first time, not when he last
 * corrected it.
 */
export const fromWord = (w: Word): Issue => ({
  id: `w-${w.key}`,
  wrong: w.wrong,
  right: w.right,
  kind: w.sentenceKind || kindFromRule(w.rule, w.wrong, w.right),
  sentence: isCurrent(w.sentenceGen) ? (w.sentence ?? '') : '',
  sentenceWrong: w.sentenceWrong || w.wrong,
  sentenceRight: w.sentenceRight || w.right,
  context: w.context ?? '',
  strategy: strategyFor(
    w.sentenceKind || kindFromRule(w.rule, w.wrong, w.right),
    w.sentenceRight || w.right,
    w.sentenceStrategy
  ),
  tip: w.sentenceTip ?? '',
  at: w.first,
  source: 'typed',
})

/**
 * Translates a LanguageTool rule into a kind of mistake.
 *
 * The rules say WHAT was objected to, the kinds of mistake say HOW to
 * practise it. For capitalisation there is a rule of its own; for everything
 * else the comparison of wrong and right tells more than the rule name,
 * because GERMAN_SPELLER_RULE sits under every second word mistake.
 */
export function kindFromRule(rule: string, wrong: string, right: string): string {
  if (/^(UPPERCASE_SENTENCE_START|DE_CASE|CASING)/.test(rule)) return 'gross-klein'
  // das/dass and its relatives is a question of grammar, not of sounds.
  // Without this line “doppelkonsonant” would come out — literally correct
  // (an s is missing) and pedagogically wrong: which of the two words is
  // meant depends on the sentence and not on the sound.
  if (/^(KRITZL_DASS|CONFUSED_|DE_CONFUSION|DOPPELTES_VERB)/.test(rule)) return 'verwechselt'
  // Same letters, different case: a question of upper and lower case,
  // whatever the rule may say about it.
  if (wrong.toLowerCase() === right.toLowerCase() && wrong !== right) return 'gross-klein'
  return kindFromDiff(wrong, right)
}

/**
 * Derives the kind of mistake from the comparison.
 *
 * The same categories that the model assigns for the photos — but computed
 * here from the two words. The order decides: the first matching rule wins,
 * and the specific ones come first.
 */
export function kindFromDiff(wrong: string, right: string): string {
  const a = wrong.toLowerCase()
  const b = right.toLowerCase()
  const withoutUmlaut = (t: string) =>
    t.replace(/ä/g, 'a').replace(/ö/g, 'o').replace(/ü/g, 'u')

  if (a.includes(' ') !== b.includes(' ')) return 'getrennt-zusammen'
  if (withoutUmlaut(a) === withoutUmlaut(b) && a !== b) return 'umlaut'
  if (a.replace(/h/g, '') === b.replace(/h/g, '') && a !== b) return 'dehnungs-h'
  if (undoubled(a) === undoubled(b) && a !== b) return 'doppelkonsonant'
  if (a.replace(/ie/g, 'i') === b.replace(/ie/g, 'i') && a !== b) return 'ie-i'
  if (sibilant(a) === sibilant(b) && a !== b) return 's-ss-ß'
  if (b.startsWith(a) || a.startsWith(b)) return 'endung'
  if (a.replace(/[dt]/g, 't') === b.replace(/[dt]/g, 't') && a !== b) return 'd-t'
  return 'nach-gehoer'
}

const undoubled = (t: string) => t.replace(/([a-zäöüß])\1/g, '$1')
const sibilant = (t: string) => t.replace(/ß/g, 's').replace(/ss/g, 's')

/** The three forms. */
export type ExerciseKind =
  | 'finden'
  | 'gross-klein'
  | 'schreibweise'
  /** Two sentences, one of them is right — see `stimmt` below. */
  | 'stimmt'
  /** The word opened at the place of the mistake — see `ergaenzen`. */
  | 'ergaenzen'
  /** Which strategy finds the spelling of this word? */
  | 'strategie'
  /** Three spellings, one of them is not a word. */
  | 'gibtsnicht'

/**
 * The strategies the exercise asks for.
 *
 * Five of the seven: the article test and the substitution test are tips
 * for the feedback line, not answers to „welche Strategie hilft?" — the
 * class test asked for the FRESCH ones, and a question with seven buttons is
 * not a question for a thumb.
 */
export const ASKED_STRATEGIES = ['Schwingen', 'Verlängern', 'Ableiten', 'Zerlegen', 'Merken'] as const

/**
 * The strategy a kind of mistake usually calls for.
 *
 * The fallback where the model named none — a sentence from before
 * generation 5 on its way out, or a stock row written without one. Coarse on
 * purpose: „dehnungs-h" is Merken here although „Zahl" could be derived from
 * „zählen"; the model knows the word and says so when it can.
 */
export function defaultStrategy(kind: string): string {
  return STRATEGY_OF_KIND[kind] ?? ''
}

const STRATEGY_OF_KIND: Record<string, string> = {
  'doppelkonsonant': 'Schwingen',
  'ie-i': 'Schwingen',
  's-ss-ß': 'Schwingen',
  'd-t': 'Verlängern',
  'umlaut': 'Ableiten',
  'dehnungs-h': 'Merken',
  // Written by ear — swinging the syllables is how the letters come back.
  'nach-gehoer': 'Schwingen',
  'getrennt-zusammen': 'Zerlegen',
  'gross-klein': 'Artikelprobe',
  'verwechselt': 'Ersatzprobe',
  // No entry for 'endung': a missing verb ending („komm" for „kommt") is
  // grammar, not spelling. No strategy finds it, so none is claimed — and the
  // word stays out of the strategy question.
}

/**
 * The strategy for THIS word — the kind's, unless the word is too short for
 * it.
 *
 * Swinging needs syllables. „Mit-schü-ler" shows every letter, but „Fuß" has
 * nothing to swing, and the school says so explicitly: one-syllable words are
 * lengthened first — Fuß → Füße, Schiff → Schiffe, Ass → Asse — and only then
 * does the vowel show its length. The answer the class test wants for them is
 * therefore Verlängern, even for s-ss-ß and a double consonant.
 *
 * Elmar found this one: the app offered „Schwingen" for „Maß" and „dick", and
 * he knew that was not what he had been taught.
 *
 * Lengthening only helps where the mistake sits at the end or in the vowel.
 * „Pferd" lengthened is „Pferde", and the Pf is as silent as before — a
 * one-syllable word written by ear is one to remember.
 *
 * @param given  The strategy the model or the catalogue named, if any. It is
 *               corrected the same way: the model said „Schwingen" for „Maß"
 *               too.
 */
export function strategyFor(kind: string, word: string, given = ''): string {
  const strategy = given || defaultStrategy(kind)
  if (strategy !== 'Schwingen' || syllables(word) > 1) return strategy
  return kind === 'nach-gehoer' ? 'Merken' : 'Verlängern'
}

/**
 * How many syllables a word has — roughly.
 *
 * Counted as vowel groups: „Schiff" one, „Vo-gel" two. A group of three or
 * more vowels is two syllables („Bau-er", „Feu-er"), and that is as far as
 * it goes; „Fa-mi-li-e" counts one short, which does no harm here. The only
 * question asked is „one, or more than one?".
 */
export function syllables(word: string): number {
  const w = word.toLowerCase()
  const groups = w.match(/[aeiouäöüy]+/g) ?? []
  const n = groups.reduce((n, g) => n + (g.length >= 3 ? 2 : 1), 0)
  // The prefix ge- before a vowel is its own syllable: ge-ahnt, ge-öffnet,
  // ge-übt. Not before i — Geige and Geist are one.
  return /^ge[aouäöü]/.test(w) ? n + 1 : n
}

/**
 * Does the dictionary know this word?
 *
 * Set by the screen from the local spell checker. Here, in the logic, there
 * is no browser and no dictionary — and the exercise „welches Wort gibt es
 * nicht?" cannot be built without one. Unset means: that form drops out.
 */
let knowsWord: ((w: string) => boolean) | null = null

export function setWordCheck(fn: ((w: string) => boolean) | null): void {
  knowsWord = fn
}

export interface Exercise {
  /** The id of the mistake it comes from. */
  id: string
  kind: ExerciseKind
  issue: Issue
  /**
   * All mistakes that belong to this exercise.
   *
   * With upper and lower case more than one: “Der ausflug in den zoo” has
   * two, and whoever sees the sentence in front of them corrects both. An
   * exercise that counts only one of the two is not hard — it is wrong. All
   * other forms have exactly one entry.
   */
  issues: Issue[]
  /** The sentence, split into words — with 'finden' and 'gross-klein'. */
  tokens?: Token[]
  /** The possibilities — with 'schreibweise'. */
  options?: string[]
  /** The beginning of the word that stands there — with 'ergaenzen'. */
  stem?: string
  /** The endings to choose from — with 'ergaenzen'. */
  parts?: string[]
  /** Is the sentence shown the correct one? — with 'stimmt'. */
  isRight?: boolean
  /** The spelling that is not a word — with 'gibtsnicht'. The options hold all three. */
  nonWord?: string
}

/** A word in the sentence, with everything the display needs. */
export interface Token {
  /** The text as it stands there. */
  text: string
  /** Punctuation behind it that should not be tapped. */
  trailing: string
  /** Is THIS word one of the wrong ones? */
  isTarget: boolean
  /** Position in the sentence — as a key for the display. */
  i: number
}

/**
 * Which form fits this mistake?
 *
 * `gross-klein` gets the toggle exercise as soon as a sentence is there:
 * choosing a word upper or lower case on its own would be pointless, because
 * the answer depends on where it stands.
 *
 * Everything else gets “finden” and “schreibweise” in turn — the first
 * practises noticing, the second writing. The decision falls on the counter,
 * not at random: this way five identical exercises do not follow one another.
 */
export function kindFor(f: Issue, nr: number): ExerciseKind {
  // The round may have decided — then that is it, as long as it still fits.
  if (f.form && fits(f.form, f)) return f.form

  const sentence = hasUsableSentence(f)

  // Without a sentence only the forms that work on the word alone.
  if (!sentence) {
    const alone: ExerciseKind[] = ['schreibweise']
    if (fits('ergaenzen', f)) alone.push('ergaenzen')
    if (fits('strategie', f)) alone.push('strategie')
    if (fits('gibtsnicht', f)) alone.push('gibtsnicht')
    return alone[nr % alone.length]!
  }

  // The forms this mistake can be practised in at all.
  //
  // Not every form fits every mistake: „welches Wort ist falsch?" has no
  // answer when the word looks right on its own, and completing a word needs
  // a place that can be opened.
  const wanted: ExerciseKind[] = [
    'finden', 'schreibweise', 'stimmt', 'ergaenzen', 'gross-klein', 'strategie', 'gibtsnicht',
  ]
  const usable = wanted.filter((k) => fits(k, f))
  if (!usable.length) return 'stimmt'

  // The toggling exercise gets a second place in the turn.
  //
  // It was the rarest form by a distance: measured on the real set it came
  // to 4.2 % of all exercises, about every twenty-fourth. Not because
  // anything picked wrongly — the counter spreads evenly, and 3 of the 12
  // fitting mistakes landing on it is exactly its share. It is a
  // multiplication: less than a quarter of the mistakes are capitalisation,
  // and of those only every fourth round takes this form.
  //
  // Standing twice, it comes every second round instead of every fourth, so
  // roughly one exercise in ten. Twice and not exclusively: it used to be
  // the only form for these mistakes, and since most of his are
  // capitalisation, four toggling sentences followed one another. Two places
  // out of five keep the other forms in the rotation.
  if (f.kind === 'gross-klein' && usable.includes('gross-klein')) {
    usable.splice(1, 0, 'gross-klein')
  }

  // Chosen out of THOSE by the counter — not searched forwards through a
  // fixed list, and not decided by the kind of mistake.
  //
  // Both were tried. Letting the kind decide first made „gross-klein" take
  // every capitalisation mistake, and since most of his are, four toggling
  // sentences came one after another. Searching forwards instead piled the
  // misses on whichever form happened to catch them: „finden" nine of
  // twenty-four, then „stimmt" ten, then „gross-klein" ten, each with runs
  // of three and four. A mistake that fits only two forms lands on the same
  // one of them every time.
  //
  // Counting WITHIN the fitting forms spreads them evenly: whatever a
  // mistake can do, it does in turn. Measured on a capitalisation-heavy set
  // that gives all five forms and no run longer than two.
  //
  // Not at random: the counter keeps the sequence the same while an exercise
  // is being answered. A form that changed under the finger would swap the
  // buttons mid-tap.
  return usable[nr % usable.length]!
}

/** Can this mistake be practised in this form? */
function fits(kind: ExerciseKind, f: Issue): boolean {
  if (kind === 'finden') {
    // „Welches Wort ist falsch?" needs a word that IS wrong. With
    // capitalisation every word looks right on its own, and with a gap
    // between two words neither of them is the wrong one.
    return f.kind !== 'gross-klein' && f.kind !== 'getrennt-zusammen'
  }

  if (kind === 'schreibweise') {
    // Not for capitalisation. „schlüssel" against „Schlüssel" under a
    // sentence with a gap is answerable, but it is not a question: the two
    // buttons differ in one letter's height, and whoever reads them has the
    // answer before thinking about the sentence. It was let through for the
    // variety — most of his mistakes are capitalisation, and without it
    // those had only two forms. The toggling form is the better one of the
    // two by a distance, so it stands twice in the turn instead.
    return f.kind !== 'gross-klein'
  }

  if (kind === 'stimmt') {
    // Works with every sentence — that is why it is the fallback everywhere.
    // With capitalisation too: the sentence stands there as written, and
    // whether it is right is a fair question.
    return true
  }

  if (kind === 'gross-klein') {
    // The toggling exercise puts the WHOLE sentence in lower case and wants
    // every word that belongs capitalised. That only makes sense when the
    // mistake really is one of capitalisation — otherwise the word he got
    // wrong would not be part of the exercise at all.
    return f.kind === 'gross-klein'
  }

  if (kind === 'strategie') {
    // Only with a strategy the exercise has a button for. The article test
    // and the substitution test are explained in the tip, not asked.
    //
    // And only on a word of its own: „welche Strategie hilft bei ‚wider'?"
    // is answerable only with the sentence, and the sentence gives away the
    // other half of that question.
    return (ASKED_STRATEGIES as readonly string[]).includes(f.strategy) &&
      !f.sentenceRight.includes(' ') && f.kind !== 'verwechselt' && f.kind !== 'gross-klein'
  }

  if (kind === 'gibtsnicht') {
    // Needs the dictionary, and a set of three in which two are words.
    if (!knowsWord) return false
    return nonWord(f.sentenceRight, f.sentenceWrong, f.kind, f.id, f.source, knowsWord) !== null
  }

  // „ergaenzen" needs a word that can be opened at the place of the mistake,
  // with a wrong ending that holds up.
  return endings(f.sentenceRight, f.sentenceWrong, f.kind, f.id, f.source).length > 1
}

/**
 * Does this mistake come into question as an exercise at all?
 *
 * Upper and lower case without a sentence is not an answerable question:
 * whether “affen” belongs upper case depends on where it stands. Such
 * mistakes wait until an exercise sentence is there — better no exercise
 * than an unfair one.
 */
export function usable(f: Issue): boolean {
  // No exercise without a sentence from the model. Not for any kind.
  //
  // What LanguageTool reports is a pair of words, and that pair carries
  // whatever the checker happened to suggest: „Nordlich → Nördlich", although
  // capitalised „Nördlich" is not a word at all, or „stralung → Strahlung",
  // which mixes the missing h with the capitalisation. Asking „wie schreibt
  // man das?" about such a pair produces a question in which no answer is
  // right, or one where a right answer proves nothing.
  //
  // The model sorts this out — it puts the word into a sentence and says
  // which form is wrong and which is right there. Until it has, there is no
  // exercise. The app fetches in the background, so this is a wait, not a
  // loss.
  if (!hasUsableSentence(f)) return false

  // A gap read out of the handwriting is no exercise.
  //
  // On paper the space between two letters comes from the movement of the
  // hand, not from a decision — “Haus aufgabe” is how it was written down,
  // not how he thinks it is spelled. The photo route no longer reports such
  // findings at all; this catches the ones already stored. When typing the
  // gap IS a decision, so there the exercise stays.
  if (f.source === 'photo' && f.kind === 'getrennt-zusammen') return false

  return true
}

/**
 * Is the sentence fit to be an exercise?
 *
 * It must really contain the wrong word — the model sometimes copies the
 * context down slightly differently — and it must not be so long that one has
 * to scroll on the phone.
 */
function hasUsableSentence(f: Issue): boolean {
  if (!f.sentence) return false
  if (f.sentence.length > 140) return false
  return f.sentence.includes(f.sentenceWrong)
}

/** Builds the exercise for a mistake. */
export function exerciseFor(f: Issue, nr: number, siblings: Issue[] = []): Exercise {
  const kind = kindFor(f, nr)
  if (kind === 'schreibweise') {
    // With this form the sentence is only shown if it is fit for it — and
    // only then do the variants make sense that one overlooks while reading
    // along (“fahreh”).
    const withSentence = hasUsableSentence(f)
    return {
      id: f.id,
      kind,
      issue: f,
      issues: [f],
      options: choices(f.sentenceRight, f.sentenceWrong, f.kind, f.id, f.source, withSentence),
    }
  }

  if (kind === 'ergaenzen') {
    return {
      id: f.id,
      kind,
      issue: f,
      issues: [f],
      stem: opened(f.sentenceRight, f.sentenceWrong)?.stem ?? '',
      // Shuffled by the id, not at random: otherwise the endings would swap
      // places under the finger as soon as anything else redraws the view.
      parts: endings(f.sentenceRight, f.sentenceWrong, f.kind, f.id, f.source),
    }
  }

  if (kind === 'strategie') {
    return {
      id: f.id,
      kind,
      issue: f,
      issues: [f],
      // Four buttons: the right strategy and three of the others, in an
      // order that holds still while the exercise stands there.
      options: strategyChoices(f.strategy, f.id),
    }
  }

  if (kind === 'gibtsnicht') {
    const set = knowsWord
      ? nonWord(f.sentenceRight, f.sentenceWrong, f.kind, f.id, f.source, knowsWord)
      : null
    return {
      id: f.id,
      kind,
      issue: f,
      issues: [f],
      options: set?.options ?? [f.sentenceRight, f.sentenceWrong],
      nonWord: set?.missing ?? f.sentenceWrong,
    }
  }

  if (kind === 'stimmt') {
    return {
      id: f.id,
      kind,
      issue: f,
      issues: [f],
      // Half of the sentences are correct.
      //
      // Whoever is shown only wrong ones learns to tap „stimmt nicht" without
      // reading. Which one it is depends on the id, so the answer stays the
      // same for as long as the exercise stands there — and is not
      // predictable from the outside.
      isRight: hash(f.id) % 2 === 0,
    }
  }

  // With upper and lower case all mistakes of the same sentence count too.
  // Otherwise a visibly wrong word stands next to it that does not count as a
  // mistake — and whoever corrects it is told they were wrong.
  const all = kind === 'gross-klein'
    ? [f, ...siblings.filter((g) => g.id !== f.id && g.kind === 'gross-klein')]
    : [f]

  return {
    id: f.id,
    kind,
    issue: f,
    issues: all,
    // With upper and lower case the whole sentence stands there in lower
    // case, and EVERY word to be capitalised is a target. With “finden” the
    // sentence stays as it is — there the point is to discover the one wrong
    // word.
    tokens: kind === 'gross-klein'
      ? splitForCase(f.sentence, f.sentenceWrong, f.sentenceRight)
      : splitSentence(f.sentence, all.map((x) => x.sentenceWrong)),
  }
}

/**
 * Splits a sentence for upper and lower case — everything lower case.
 *
 * The sentence comes from the model and is correct apart from the one
 * mistake. If it stayed like that, it would give the answer away: everything
 * correct already stands there in upper case, so the one lower-case word
 * stands out, and one finds it without knowing the rule.
 *
 * Written in lower case it becomes a real exercise. Every word wants to be
 * judged on its own — the first one too, because the start of the sentence
 * belongs to the rule, and the many that stay lower case as well.
 *
 * The target is therefore every word that stands in upper case in the
 * ORIGINAL, no longer just the one that was reported.
 */
export function splitForCase(sentence: string, wrong = '', right = ''): Token[] {
  // First correct the mistake: the sentence contains the word in lower case
  // because it is built for the other exercise forms. For THIS form it has to
  // be correct — otherwise the practised word itself could not be recognised
  // as one to be capitalised, and the exercise would have no right solution.
  //
  // `replaceAll`, not `replace`: the same word can stand twice in the
  // sentence, and a leftover lower-case copy would count as "stays small" —
  // the child capitalises it correctly and is told it was wrong.
  const correct = wrong && right ? sentence.replaceAll(wrong, right) : sentence
  const parts = correct.split(/\s+/).filter(Boolean)
  return parts.map((t, i) => {
    const m = /^([^\p{L}\p{N}]*)(.*?)([^\p{L}\p{N}]*)$/u.exec(t)
    const head = m?.[1] ?? ''
    const core = m?.[2] ?? t
    const trailing = m?.[3] ?? ''
    const c = core[0]
    const upper = !!c && c === c.toUpperCase() && c !== c.toLowerCase()
    return {
      // Show everything in lower case — that is the exercise.
      text: head + (upper ? (c ?? '').toLowerCase() + core.slice(1) : core),
      trailing,
      isTarget: upper,
      i,
    }
  })
}

/**
 * Splits a sentence into tappable words.
 *
 * Punctuation stays stuck to the word but is kept separately: whoever taps
 * “Hause.” means “Hause” — and when toggling to upper case the full stop must
 * not travel along.
 *
 * @param wrong  One or several wrong words. Several with upper and lower
 *   case, where one sentence can have several mistakes.
 */
export function splitSentence(sentence: string, wrong: string | string[]): Token[] {
  const wanted = Array.isArray(wrong) ? [...wrong] : [wrong]
  const parts = sentence.split(/\s+/).filter(Boolean)
  return parts.map((t, i) => {
    // Split off everything at the front and the back that is not a letter.
    const m = /^([^\p{L}\p{N}]*)(.*?)([^\p{L}\p{N}]*)$/u.exec(t)
    const head = m?.[1] ?? ''
    const core = m?.[2] ?? t
    const trailing = m?.[3] ?? ''
    // Hit every wanted word only ONCE: if the same one stands twice in the
    // sentence, the second place is not the mistake that was reported.
    const j = wanted.indexOf(core)
    const isTarget = j >= 0
    if (isTarget) wanted.splice(j, 1)
    return { text: head + core, trailing, isTarget, i }
  })
}

/**
 * The order of the exercises.
 *
 * The oldest first: what has lain around longer is more likely forgotten.
 * Within the same page the order of the text remains — this way the exercise
 * incidentally gives the course of the page that he wrote.
 *
 * Solved ones do not come again. Not because they would have sunk in — that
 * would need a repetition procedure — but because an exercise that shows the
 * same ten words endlessly stops being an exercise.
 */
export function unsolved(all: readonly Mistake[]): Mistake[] {
  return all
    .filter((m) => m.solved === null)
    .sort((a, b) => a.at - b.at || a.id.localeCompare(b.id))
}

/**
 * All mistakes from both sources, in one row.
 *
 * The photographed pages bring `Mistake`, the typed texts `Word`. A word
 * counts as open as long as it has not already been solved as an exercise —
 * the list of the solved ones stands in `solved`.
 *
 * Words without a correct spelling drop out: from a correction that has
 * nothing to compare, no exercise can be built.
 */
export function allIssues(
  mistakes: readonly Mistake[],
  words: readonly Word[],
  solved: ReadonlySet<string>
): Issue[] {
  const fromPhotos = unsolved(mistakes)
    .filter((m) => !missingDots(m))
    .flatMap((m) => withExtras(fromPhoto(m), m.sentenceMore))
  const fromTexts = words
    .filter((w) => w.right && w.wrong && w.right !== w.wrong)
    .flatMap((w) => withExtras(fromWord(w), w.sentenceMore))
    // By the base id: a derived exercise belongs to the same mistake, and
    // that one is either practised or it is not.
    .filter((f) => !solved.has(baseId(f.id)))
  // The NEWEST first. It used to be the oldest, on the thought that what
  // has waited longest is most overdue — and the effect was that the class
  // test photographed today came after thirteen exercises from texts of two
  // weeks ago, and the sitting felt like it had not noticed the upload.
  // What he has just written or photographed is what he is waiting for;
  // the old ones are still in the round, behind it, and the clock decides
  // how far he gets.
  return once(
    [...fromPhotos, ...fromTexts]
      .filter(usable)
      .sort((a, b) => b.at - a.at || a.id.localeCompare(b.id))
  )
}

/**
 * Every word once, every sentence once.
 *
 * The same mistake can arrive more than once: a page photographed twice, a
 * photographed page that was then read again as a text, a typed word that the
 * review of the whole text found as well. Each copy is a row of its own, with
 * its own id and its own exercise sentences — and a round then shows
 * „Erdhalbkugel" three times, in the same sentence. Measured on Elmar's first
 * real sitting: most of what he was shown were such copies, and the round
 * felt too easy because he had already answered each of them.
 *
 * Kept is the oldest. The others are not thrown away: once the first is
 * solved, the next one is free to come up in a later round.
 *
 * Capitalisation is the one exception for the sentence: several of its
 * mistakes can hang on ONE sentence on purpose, and the round puts them
 * together into one exercise.
 */
export function once(issues: readonly Issue[]): Issue[] {
  // His own mistakes have the first claim. The derived exercises carry a
  // „+n" and practise the rule on another word; when one of those and a
  // real mistake of his are the same word, the real one stays and the
  // derived one waits — whichever is newer. Decided on the reordered list,
  // returned in the order given.
  const derived = (f: Issue) => /\+\d+$/.test(f.id)
  const keep = new Set(
    pickOnce([...issues.filter((f) => !derived(f)), ...issues.filter(derived)]).map((f) => f.id)
  )
  return issues.filter((f) => keep.has(f.id))
}

function pickOnce(issues: readonly Issue[]): Issue[] {
  const words = new Set<string>()
  const sentences = new Set<string>()
  const stems: string[] = []
  return issues.filter((f) => {
    const word = `${f.sentenceWrong.toLowerCase()}→${f.sentenceRight.toLowerCase()}`
    const sentence = f.sentence.trim().toLowerCase()
    if (words.has(word)) return false
    if (sentence && f.kind !== 'gross-klein' && sentences.has(sentence)) return false
    // The same word in another coat is the same word. „Sonnenstrahlung",
    // „Strahl" and „Stralung → Strahlung" are one lesson about one silent h,
    // and a round that asked all three in twenty exercises felt like it knew
    // only that word. One of the family per round; the rest wait.
    const stem = f.sentenceRight.toLowerCase()
    if (stem.length >= 4 && stems.some((x) => x.includes(stem) || stem.includes(x))) return false
    words.add(word)
    if (sentence) sentences.add(sentence)
    if (stem.length >= 4) stems.push(stem)
    return true
  })
}

/**
 * Umlaut dots missing on a photographed page.
 *
 * On paper those are dots that came out too small, too faint or too far off
 * — not a child who does not know what an ö is. „Nordlich" for „nördlich"
 * came up in his exercises as a mistake to practise, and it was the
 * handwriting recognition's, not his. The photo route no longer reports
 * them; this catches the ones already stored. „Equater" for „Äquator" stays:
 * there more than the dots is different.
 */
function missingDots(m: Mistake): boolean {
  if (!m.pageId.startsWith('foto-')) return false
  const plain = (t: string) =>
    t.toLowerCase().replace(/ä/g, 'a').replace(/ö/g, 'o').replace(/ü/g, 'u')
  return m.wrong.toLowerCase() !== m.right.toLowerCase() && plain(m.wrong) === plain(m.right)
}

/**
 * The id of the mistake an exercise belongs to.
 *
 * Derived exercises carry a "+0" — they practise the same rule on another
 * word. For "is it solved?" only the mistake counts, not the single sentence.
 */
export const baseId = (id: string): string => id.replace(/\+\d+$/, '')

/**
 * One mistake, several exercises.
 *
 * Whoever forgets the double consonant in „schwimt" forgets it in „komt" too.
 * The model therefore writes two more sentences for the same rule on other
 * words, and they become exercises of their own — so a round does not show
 * the same word three times in different sentences.
 *
 * They count as solved together with the first: the rule is what is being
 * practised, and once it sits it sits.
 */
function withExtras(base: Issue, more?: MoreExercise[]): Issue[] {
  if (!more?.length) return [base]
  return [
    base,
    ...more.map((m, i) => ({
      ...base,
      id: `${base.id}+${i}`,
      sentence: base.sentence ? m.sentence : '',
      sentenceWrong: m.wrong,
      sentenceRight: m.right,
      kind: m.kind || base.kind,
      strategy: strategyFor(m.kind || base.kind, m.right, m.strategy),
      tip: m.tip ?? '',
    })),
  ]
}

/**
 * Mistakes that still lack an exercise sentence.
 *
 * The app fetches it in the background — see `api/exercises.js`. Until then they
 * do not turn up in the exercises.
 *
 * Only the mistakes the MODEL found: the photographed pages and the typed
 * texts it has read as a whole on the start screen. The words recorded while
 * typing are no longer sent. The review reads the same text in one piece, so
 * what they would bring is already there — and what they bring on their own
 * is mostly noise: „aum → Baum" is not a mistake, it is the moment between
 * deleting the „b" and typing the „B".
 *
 * @param tried  Mistakes already sent in this session. The model may find
 *   nothing in a sentence, and then the mistake stays without a sentence. Sent
 *   again on every sync, the same six would take every call — and the text he
 *   wrote today would never get its turn.
 */
export function withoutSentence(
  mistakes: readonly Mistake[],
  tried: ReadonlySet<string> = new Set()
): Issue[] {
  return unsolved(mistakes)
    .filter((m) => !missingDots(m))
    .map(fromPhoto)
    // A sentence of his own for the model to read — or, from a worksheet,
    // the bare word: that one goes up as a line of its own, see `wordLine`.
    .filter((f) => !f.sentence && (f.context.length > 10 || !!wordLine(f)) && !tried.has(f.id))
    // The NEWEST first. What he has just written is what he is waiting for
    // — the same order the review uses, and for the same reason.
    .sort((a, b) => b.at - a.at || a.id.localeCompare(b.id))
}

/**
 * What goes to the model for a mistake that has no sentence.
 *
 * A class test's s/ss/ß table has no sentence to send: „Mass" circled where
 * „Maß" was right. The model is told the pair in a form it recognises and
 * builds the exercises as it does from a sentence — the prompt describes
 * this line. Empty when there is no pair to describe.
 */
export function wordLine(f: Issue): string {
  if (!f.wrong.trim() || !f.right.trim() || f.wrong === f.right) return ''
  return `Wort: ${f.right} — er hielt für richtig: ${f.wrong}`
}

/**
 * The text that is sent for a mistake: his sentence, or the word line.
 *
 * A context with a „|" or a bracket in it is a table row or a transcription
 * note off a worksheet, not a sentence — the server no longer stores those,
 * but the ones already stored must not go to the model as his sentence.
 */
export const contextFor = (f: Issue): string =>
  f.context.length > 10 && !/[|[\]]/.test(f.context) ? f.context : wordLine(f)

/**
 * Pulls the variants of one mistake apart.
 *
 * Three exercises hang off every mistake — his word and two others that
 * practise the same rule. Sorted by age and then by id they stand directly
 * one after another, and that is exactly how it feels: three times the silent
 * h in a row, and by the second one you know what is being asked without
 * reading the word.
 *
 * Spread out instead: all the first variants come first, then all the second
 * ones, then the third. Between two exercises on the same rule there are as
 * many others as there are open mistakes — and the round still begins with
 * the oldest.
 *
 * The order within a group stays as it was, so it remains reproducible: the
 * same open mistakes give the same sequence, and an exercise does not jump to
 * another place while it is being answered.
 */
export function spread(issues: readonly Issue[]): Issue[] {
  const groups = new Map<number, Issue[]>()
  for (const f of issues) {
    // "id+2" is the third variant, a plain id the first.
    const m = /\+(\d+)$/.exec(f.id)
    const rank = m?.[1] ? Number(m[1]) + 1 : 0
    const list = groups.get(rank) ?? []
    list.push(f)
    groups.set(rank, list)
  }
  return [...groups.keys()]
    .sort((a, b) => a - b)
    .flatMap((rank) => groups.get(rank) ?? [])
}

/**
 * Puts something else between his own exercises.
 *
 * The stock used to fill up only behind his own mistakes, and with three
 * exercises hanging off each of thirty mistakes it never came up at all:
 * ninety of his before the first of the catalogue, and the catalogue's rows
 * for his class — the nominalisations, the s/ss/ß pairs — were never seen.
 * Every `every`-th place now takes one from the second list, until that
 * one runs out. His own stay in front and in their order.
 */
export function weave<T>(own: readonly T[], extra: readonly T[], every = 4): T[] {
  if (!extra.length) return [...own]
  const out: T[] = []
  let e = 0
  own.forEach((f, i) => {
    out.push(f)
    if ((i + 1) % every === 0 && e < extra.length) out.push(extra[e++]!)
  })
  while (e < extra.length) out.push(extra[e++]!)
  return out
}

/**
 * The four strategies to choose from: the right one and three others.
 *
 * Shuffled by the id, like every other set of buttons here — the same
 * exercise must show the same order for as long as it stands there.
 */
export function strategyChoices(right: string, seed: string): string[] {
  const others = shuffle(ASKED_STRATEGIES.filter((s) => s !== right), seed).slice(0, 3)
  return shuffle([right, ...others], seed + '#')
}

/**
 * A number from a string — stable across sessions.
 *
 * Decides for „stimmt" whether the correct or the wrong sentence is shown.
 * `Math.random()` would swap the sentence on every redraw, and a counter
 * would make it predictable: right, wrong, right, wrong.
 */
function hash(s: string): number {
  let h = 2166136261
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return Math.abs(h)
}

/**
 * Makes sure every strategy that is in the round gets asked.
 *
 * Elmar noticed it: in his whole round the strategy question knew only
 * Schwingen and Merken. Not because his mistakes had no other — Schlusssatz
 * is Zerlegen, erzählt is Ableiten — but because the form is dealt by the
 * counter, and in 76 exercises it fell seven times, six of them on a
 * catalogue row. The catalogue is weighted by HIS kinds, and his kinds are
 * the swinging kinds. The strategies he did not know in the class test were
 * exactly the ones that never came up.
 *
 * So, after the round is laid: each of the five asked strategies that any
 * issue in the round carries is asked at least once. Where the counter did
 * not give it a question, the earliest issue with that strategy that fits
 * the form gets it — his own mistakes before the catalogue, since those are
 * the words it is about. Only that one position changes; the counter decides
 * everything else as before, so the order stays as still as it was.
 */
export function askEveryStrategy(round: readonly Issue[]): Issue[] {
  const out = [...round]
  const asked = new Set<string>()
  out.forEach((f, i) => {
    if (kindFor(f, i) === 'strategie') asked.add(f.strategy)
  })
  const isStock = (f: Issue) => f.id.startsWith('stock-')
  for (const strategy of ASKED_STRATEGIES) {
    if (asked.has(strategy)) continue
    const candidates = out
      .map((f, i) => ({ f, i }))
      .filter(({ f, i }) => f.strategy === strategy && !f.form && fits('strategie', f) && kindFor(f, i) !== 'strategie')
    // His own first, then by position.
    const pick = candidates.find(({ f }) => !isStock(f)) ?? candidates[0]
    if (!pick) continue
    out[pick.i] = { ...pick.f, form: 'strategie' }
    asked.add(strategy)
  }
  return out
}
